import { authService } from './authService';

export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  role: 'PATIENT' | 'CAREGIVER' | 'ADMIN';
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserCounts {
  total: number;
  byRole: {
    patients: number;
    caregivers: number;
    admins: number;
  };
  disabled: number;
  active: number;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: PaginationInfo;
  timestamp: string;
}

export interface UserCountsResponse {
  success: boolean;
  data: UserCounts;
  timestamp: string;
}

export interface BulkActionRequest {
  userIds: string[];
  action: 'delete' | 'disable' | 'enable';
}

export interface BulkActionResponse {
  success: boolean;
  data: { affectedCount: number };
  message: string;
  timestamp: string;
}

export interface SearchUsersParams {
  query: string;
  role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN';
  disabled?: boolean;
  page?: number;
  limit?: number;
}

class UserManagementService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://sharegiver-api.web.app';
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = authService.getToken();
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all users with pagination
  async getUsers(page: number = 1, limit: number = 10): Promise<UsersResponse> {
    return this.makeRequest<UsersResponse>(`/users?page=${page}&limit=${limit}`);
  }

  // Get user by ID
  async getUserById(id: string): Promise<{ success: boolean; data: User; timestamp: string }> {
    return this.makeRequest(`/users/${id}`);
  }

  // Update user
  async updateUser(id: string, updates: Partial<Pick<User, 'firstName' | 'lastName' | 'avatar'>>): Promise<{ success: boolean; data: User; message: string; timestamp: string }> {
    return this.makeRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Update user role
  async updateUserRole(id: string, role: 'PATIENT' | 'CAREGIVER' | 'ADMIN'): Promise<{ success: boolean; data: User; message: string; timestamp: string }> {
    return this.makeRequest(`/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Update user status (disable/enable)
  async updateUserStatus(id: string, disabled: boolean): Promise<{ success: boolean; data: User; message: string; timestamp: string }> {
    return this.makeRequest(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ disabled }),
    });
  }

  // Delete user
  async deleteUser(id: string): Promise<{ success: boolean; message: string; timestamp: string }> {
    return this.makeRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Bulk actions
  async bulkAction(request: BulkActionRequest): Promise<BulkActionResponse> {
    return this.makeRequest<BulkActionResponse>('/users/bulk-action', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Get user counts
  async getUserCounts(): Promise<UserCountsResponse> {
    return this.makeRequest<UserCountsResponse>('/users/stats/counts');
  }

  // Search users
  async searchUsers(params: SearchUsersParams): Promise<UsersResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('q', params.query);
    
    if (params.role) {
      searchParams.append('role', params.role);
    }
    
    if (params.disabled !== undefined) {
      searchParams.append('disabled', params.disabled.toString());
    }
    
    if (params.page) {
      searchParams.append('page', params.page.toString());
    }
    
    if (params.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    return this.makeRequest<UsersResponse>(`/users/search?${searchParams.toString()}`);
  }

  // Get users by role
  async getUsersByRole(role: 'PATIENT' | 'CAREGIVER' | 'ADMIN', page: number = 1, limit: number = 10): Promise<UsersResponse> {
    return this.makeRequest<UsersResponse>(`/users/by-role/${role}?page=${page}&limit=${limit}`);
  }

  // Helper methods
  getUserDisplayName(user: User): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email;
  }

  getUserInitials(user: User): string {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  }

  getRoleColor(role: 'PATIENT' | 'CAREGIVER' | 'ADMIN'): string {
    switch (role) {
      case 'PATIENT':
        return '#2196F3'; // Blue
      case 'CAREGIVER':
        return '#FF9800'; // Orange
      case 'ADMIN':
        return '#F44336'; // Red
      default:
        return '#757575'; // Gray
    }
  }

  getRoleLabel(role: 'PATIENT' | 'CAREGIVER' | 'ADMIN'): string {
    switch (role) {
      case 'PATIENT':
        return 'Patient';
      case 'CAREGIVER':
        return 'Caregiver';
      case 'ADMIN':
        return 'Admin';
      default:
        return 'Unknown';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export const userManagementService = new UserManagementService();
